import dayjs from 'dayjs';

/**
 * Validates if a date string is a valid date format
 * @param {string} dateString - The date string to validate
 * @returns {boolean} - True if valid date, false otherwise
 */
export const isValidDate = dateString => {
  if (!dateString) return true; // Allow empty dates
  return dayjs(dateString, 'YYYY-MM-DD', true).isValid();
};

/**
 * Validates if a date is within the allowed range (1 year ago to 90 days in future)
 * @param {string} dateString - The date string to validate
 * @returns {boolean} - True if within range, false otherwise
 */
export const isDateInAllowedRange = dateString => {
  if (!dateString) return true; // Allow empty dates

  const date = dayjs(dateString);
  const today = dayjs();
  const oneYearAgo = today.subtract(1, 'year');
  const ninetyDaysFromNow = today.add(90, 'days');

  return date.isAfter(oneYearAgo) && date.isBefore(ninetyDaysFromNow.add(1, 'day'));
};

/**
 * Validates a date field with both format and range validation
 * @param {string} dateString - The date string to validate
 * @param {string} fieldName - The name of the field for error messages
 * @returns {object} - Object with isValid boolean and error message if invalid
 */
export const validateExpenseDate = (dateString, fieldName = 'Date') => {
  if (!dateString) {
    return { isValid: true, error: null };
  }

  if (!isValidDate(dateString)) {
    return {
      isValid: false,
      error: `${fieldName} must be a valid date format (YYYY-MM-DD).`,
    };
  }

  if (!isDateInAllowedRange(dateString)) {
    return {
      isValid: false,
      error: `${fieldName} must be within the last year and not more than 90 days in the future.`,
    };
  }

  return { isValid: true, error: null };
};

/**
 * Validates a date field with only format validation (no range restrictions)
 * Used for SearchExpenses page date filters
 * @param {string} dateString - The date string to validate
 * @param {string} fieldName - The name of the field for error messages
 * @returns {object} - Object with isValid boolean and error message if invalid
 */
export const validateSearchExpenseDate = (dateString, fieldName = 'Date') => {
  if (!dateString) {
    return { isValid: true, error: null };
  }

  if (!isValidDate(dateString)) {
    return {
      isValid: false,
      error: `${fieldName} must be a valid date format (YYYY-MM-DD).`,
    };
  }

  return { isValid: true, error: null };
};

/**
 * Gets the min and max date attributes for HTML date inputs
 * @returns {object} - Object with min and max date strings
 */
export const getDateInputConstraints = () => {
  const today = dayjs();
  const oneYearAgo = today.subtract(1, 'year').format('YYYY-MM-DD');
  const ninetyDaysFromNow = today.add(90, 'days').format('YYYY-MM-DD');

  return {
    min: oneYearAgo,
    max: ninetyDaysFromNow,
  };
};

/**
 * Updates receipt errors state properly
 * @param {number} receiptId - The receipt ID
 * @param {string} fieldName - The field name
 * @param {string} errorMessage - The error message (null to clear)
 * @param {Array} currentErrors - Current errors array
 * @param {Function} setErrors - Function to update errors state
 */
export const updateReceiptError = (receiptId, fieldName, errorMessage, currentErrors, setErrors) => {
  const newErrors = [...currentErrors];

  if (!newErrors[receiptId]) {
    newErrors[receiptId] = {};
  }

  if (errorMessage) {
    newErrors[receiptId][fieldName] = errorMessage;
  } else {
    delete newErrors[receiptId][fieldName];
    // Remove the receipt error object if it's empty
    if (Object.keys(newErrors[receiptId]).length === 0) {
      delete newErrors[receiptId];
    }
  }

  setErrors(newErrors);
};
